import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  LocationOn,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import logo from '../../assets/resourses/logo.png';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const toast = useToast();
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check for logout reason (e.g., inactivity timeout)
  useEffect(() => {
    const logoutReason = sessionStorage.getItem('logoutReason');
    if (logoutReason) {
      toast.warning(logoutReason, 8000); // Show for 8 seconds
      sessionStorage.removeItem('logoutReason');
    }
  }, [toast]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = await login(username, password);
    if (result.success) {
      toast.success('Login successful!');
      const userData = JSON.parse(localStorage.getItem('user'));
      const role = userData?.role;
      
      // Map roles to correct route paths - redirect to dashboard
      let routePath = '/login';
      if (role === 'ADMIN') {
        routePath = '/admin/dashboard';
      } else if (role === 'LOAN_OFFICER') {
        routePath = '/officer/dashboard';
      } else if (role === 'FIELD_OFFICER') {
        routePath = '/collector/dashboard';
      }
      
      navigate(routePath);
    } else {
      toast.error(result.error || 'Invalid credentials');
    }
  };

  return (
    <Box
      sx={{
        minHeight: { xs: '100vh', sm: '100vh' }, // Ensure full height on mobile
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #E3F2FD 0%, #1976D2 50%, #0D47A1 100%)',
        padding: { xs: 1, sm: 2 }, // Less padding on mobile
        position: 'relative',
        overflow: 'hidden',
        // Ensure proper viewport on mobile
        WebkitOverflowScrolling: 'touch',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          animation: 'float 20s ease-in-out infinite',
          opacity: 0.3,
        },
        '@keyframes float': {
          '0%, 100%': {
            transform: 'translate(0, 0)',
          },
          '50%': {
            transform: 'translate(-50px, -50px)',
          },
        },
      }}
    >
    <Container 
      maxWidth="sm"
      sx={{
        width: '100%',
        px: { xs: 1, sm: 2 }, // Less horizontal padding on mobile
      }}
    >
        <Paper
          elevation={24}
          sx={{
            padding: { xs: 2.5, sm: 5 }, // Adjusted padding for mobile
            width: '100%',
            maxWidth: '100%',
            borderRadius: { xs: 2, sm: 4 }, // Smaller border radius on mobile
            background: '#FFFFFF',
            boxShadow: '0 8px 32px rgba(13, 71, 161, 0.3)',
            position: 'relative',
            overflow: 'hidden',
            // Ensure proper display on mobile
            boxSizing: 'border-box',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '5px',
              background: 'linear-gradient(90deg, #42A5F5 0%, #1976D2 50%, #0D47A1 100%)',
            },
          }}
        >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
              mb: 4,
        }}
      >
            <Box
              component="img"
              src={logo}
              alt="Company Logo"
              sx={{
                width: { xs: 100, sm: 120 },
                height: 'auto',
                mb: 2,
                objectFit: 'contain',
              }}
            />
            <Typography
              component="h1"
              variant="h5"
              align="center"
              sx={{
                fontWeight: 700,
                color: '#0D47A1',
                letterSpacing: '0.5px',
                mb: 0.5,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
              }}
            >
              KANDURATA DEVELOPMENT HOLDINGS
            </Typography>
            <Typography
              variant="body2"
              align="center"
              sx={{
                color: '#1976D2',
                fontWeight: 500,
                mt: 1,
              }}
            >
            Loan Management System
          </Typography>
          </Box>

          <Typography
            variant="h6"
            align="center"
            sx={{
              color: '#1976D2',
              fontWeight: 600,
              mb: 3,
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            Sign In to Your Account
          </Typography>


          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#1976D2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0D47A1',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#0D47A1',
                },
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      sx={{
                        color: '#1976D2',
                        '&:hover': {
                          color: '#0D47A1',
                        },
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#1976D2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0D47A1',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#0D47A1',
                },
              }}
            />
            {/* Location Tracking Info */}
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'info.light',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <LocationOn sx={{ color: 'info.main', fontSize: 20 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Location tracking is enabled for security. Your location will be recorded when you log in.
              </Typography>
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 4,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 15px rgba(13, 71, 161, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565C0 0%, #0A3D91 100%)',
                  boxShadow: '0 6px 20px rgba(13, 71, 161, 0.5)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Sign In
            </Button>
          </Box>
        </Paper>
      </Container>
      </Box>
  );
};

export default Login;

